import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { CommonDialogFooter } from "@/shared/components/dialog/dialog-common";
import { Plus } from "lucide-react";
import { ReactNode, useEffect, useState } from "react";
import { v4 } from "uuid";
import { NullUUID } from "@/shared/lib/utilities/uuid";
import { RFC3339timestamp, UUID } from "@bitory-gmbh/nidavellir";
import { CustomerSelect } from "@/services/backend/customers/components/customer-select";
import { Label } from "@/shared/components/ui/label";
import { ServiceObjectSelect } from "@/services/backend/customers/components/service-object-select";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { TextInput } from "@/shared/components/form/text-input";
import {
  InputValidationErrors,
  inputValidationErrorMessages,
} from "@/shared/components/errors/input-error-messages";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useHtzAirHandlingUnitPutMutation,
  PutRequest,
} from "@/services/backend/htz/ahu/api/ahu-api";

export function CreateAhuDialog({
  companyId,
  customerId,
  serviceObjectId,
  trigger = (
    <Button size="sm">
      <Plus />
      <span>{t("RLT-Anlage erstellen")}</span>
    </Button>
  ),
}: {
  companyId?: UUID;
  customerId?: string;
  serviceObjectId?: string;
  trigger?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<PutRequest>({
    id: v4(),
    name: "",
    designation: "",
    companyId: companyId || NullUUID,
    customerId: customerId || NullUUID,
    serviceObjectId: serviceObjectId || NullUUID,
    createdAt: "",
    updatedAt: "",
  });
  const [put, { isLoading, error, isSuccess, reset }] =
    useHtzAirHandlingUnitPutMutation();

  const doPut = () => {
    if (isLoading) return;
    const now = new Date().toISOString() as RFC3339timestamp;
    put({
      ...request,
      createdAt: now,
      updatedAt: now,
    });
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      setOpen(false);
      setRequest({
        id: v4(),
        name: "",
        designation: "",
        companyId: companyId || NullUUID,
        customerId: customerId || NullUUID,
        serviceObjectId: serviceObjectId || NullUUID,
        createdAt: "",
        updatedAt: "",
      });
    }
  }, [companyId, customerId, isSuccess, reset, serviceObjectId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-max">
        <DialogHeader>
          <DialogTitle>{t("RLT-Anlage erstellen")}</DialogTitle>
          <DialogDescription>
            {t("Hier können Sie eine RLT-Anlage erstellen.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>{t("Unternehmen")}</Label>
          <CompanySelect
            companyId={request.companyId}
            onCompanyIdChange={(newCompanyId) =>
              setRequest({ ...request, companyId: newCompanyId })
            }
            disabled={companyId !== undefined || isLoading}
          />
          <InputValidationErrors error={error} field="companyId" />
        </div>
        <div className="grid gap-2">
          <Label>{t("Kunde")}</Label>
          <CustomerSelect
            value={request.customerId}
            onValueChange={(newCustomerId) =>
              setRequest({ ...request, customerId: newCustomerId })
            }
            disabled={customerId !== undefined || isLoading}
          />
        </div>
        <div className="grid gap-2">
          <Label>{t("Service Objekt")}</Label>
          <ServiceObjectSelect
            value={request.serviceObjectId}
            onValueChange={(newServiceObjectId) =>
              setRequest({ ...request, serviceObjectId: newServiceObjectId })
            }
            customerId={request.customerId}
            disabled={serviceObjectId !== undefined || isLoading}
          />
        </div>
        <TextInput
          text={request.name}
          field="name"
          onTextChange={(name) => setRequest({ ...request, name })}
          label={t("Name")}
          errors={inputValidationErrorMessages(error, "name")}
          disabled={isLoading}
          hint={t(
            "Der Name sollte die Anlage für interne Zwecke eindeutig identifizieren. Er wird nicht auf Dokumenten für den Kunden genutzt.",
          )}
        />
        <TextInput
          text={request.designation}
          field="designation"
          onTextChange={(designation) =>
            setRequest({ ...request, designation })
          }
          label={t("Bezeichnung")}
          errors={inputValidationErrorMessages(error, "designation")}
          disabled={isLoading}
          hint={t(
            "Die Bezeichnung wird auf Dokumenten für den Kunden genutzt. Dies sollte möglichst der Bezeichnung auf der Anlage entsprechen",
          )}
        />
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter isLoading={isLoading} onClickAction={doPut} />
      </DialogContent>
    </Dialog>
  );
}
